import { bool, func, string } from 'prop-types';
import React from 'react';

import CloseIcon from 'ravenjs/lib/SvgIcon/types/CloseIcon';

import AlertCircleOutline from 'components/Icons/AlertCircleOutline';
import AlertTriangleIcon from 'components/Icons/AlertTriangleIcon';
import InfoIcon from 'components/Icons/InfoIcon';
import SuccessIcon from 'components/Icons/SuccessIcon';
import { BANNER_TYPE_PREVIEW_COLORS } from 'constants/banners';

import { BannerCloseButton, BannerFrame, BannerMessage } from './styles';

const ICON_SIZE = '20px';

/**
 * The Feedback icon of the banner, one per banner type and tinted with the
 * banner type icon color. Mirrors the icon used by the admin's live preview
 * (see `BannerModal/components.js`).
 *
 * @param  {Object} props The props for the component.
 * @return {JSX.Element}
 */
export const BannerTypeIcon = ({ bannerType, color }) => {
    switch (bannerType) {
        case 'success':
            return <SuccessIcon color={color} height={ICON_SIZE} width={ICON_SIZE} />;
        case 'attention_required':
            return <AlertTriangleIcon color={color} height={ICON_SIZE} width={ICON_SIZE} />;
        case 'critical_alert':
            return <AlertCircleOutline color={color} height={ICON_SIZE} width={ICON_SIZE} />;
        default:
            return <InfoIcon fill={color} height={ICON_SIZE} width={ICON_SIZE} />;
    }
};

BannerTypeIcon.propTypes = {
    bannerType: string,
    color: string,
};

BannerTypeIcon.defaultProps = {
    bannerType: 'announcement',
    color: undefined,
};

/**
 * Renders a single banner: the surface, border, icon and message all follow
 * the banner type, and the close icon is only shown when the banner is
 * dismissible. Unlike the admin's live preview (`BannerPreview` in
 * `BannerModal/components.js`), this renders the banner itself with no
 * surrounding preview container, so it can be dropped directly onto the
 * dashboard.
 *
 * @param  {Object} props The props for the component.
 * @return {JSX.Element}
 */
export const BannerPreview = ({ bannerType, dismissible, message, onDismiss }) => {
    const colors =
        BANNER_TYPE_PREVIEW_COLORS[bannerType] || BANNER_TYPE_PREVIEW_COLORS.announcement;

    return (
        <BannerFrame color={colors.border} backgroundColor={colors.background}>
            <BannerTypeIcon bannerType={bannerType} color={colors.icon} />
            <BannerMessage
                color={colors.text || colors.icon}
                dangerouslySetInnerHTML={{ __html: message }}
            />
            {dismissible && (
                <BannerCloseButton
                    type="button"
                    aria-label="Dismiss banner"
                    color={colors.icon}
                    onClick={onDismiss}
                >
                    <CloseIcon height="9.15px" width="9.15px" />
                </BannerCloseButton>
            )}
        </BannerFrame>
    );
};

BannerPreview.propTypes = {
    bannerType: string,
    dismissible: bool,
    message: string,
    onDismiss: func,
};

BannerPreview.defaultProps = {
    bannerType: 'announcement',
    dismissible: false,
    message: '',
    onDismiss: () => {},
};

export default BannerPreview;
